import { can } from '../../permissions';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { portalRequest, portalList, portalFile } from './services';
export function usePortal() {
  const params = useParams(),
    token = useSelector((s) => s.AppReducer.token),
    user = useSelector((s) => s.AppReducer.user);
  const faculdade = params.faculdade || user?.empresa?.id;
  const changed = !!user?.empresa?.change_text;
  return {
    faculdade,
    token,
    user,
    base: `/instituicao/portal/${faculdade}`,
    labels: {
      selection: changed ? 'Processo seletivo' : 'Instituição',
      selections: changed ? 'Processos seletivos' : 'Instituições',
      position: changed ? 'Vaga/Cargo' : 'Processo seletivo',
      positions: changed ? 'Vagas/Cargos' : 'Processos seletivos',
    },
  };
}
export function usePortalContext() {
  const { faculdade, token, user } = usePortal();
  return useQuery(
    ['portal', faculdade, user?.id, 'context'],
    () => portalRequest(faculdade, token, 'context').then((r) => r.data),
    {
      staleTime: 60000,
      retry: false,
    }
  );
}
export function useSelecoes() {
  const { faculdade, token, user } = usePortal(),
    context = usePortalContext();
  return useQuery(
    ['portal', faculdade, user?.id, 'selecoes'],
    () => portalRequest(faculdade, token, 'selecoes').then((r) => r.data),
    {
      enabled: !!context.data,
      retry: false,
    }
  );
}
export function useSelecao(id) {
  const { faculdade, token, user } = usePortal(),
    context = usePortalContext();
  return useQuery(
    ['portal', faculdade, user?.id, 'selecao', id],
    () => portalRequest(faculdade, token, 'selecoes/' + id).then((r) => r.data),
    {
      enabled: !!context.data && !!id && id !== 'nova',
      retry: false,
    }
  );
}
export function useInscricoes(filters, enabled = true) {
  const { faculdade, token, user } = usePortal(),
    context = usePortalContext();
  return useQuery(
    ['portal', faculdade, user?.id, 'inscricoes', filters],
    () => portalList(faculdade, token, 'inscricoes', filters),
    {
      enabled: !!context.data && enabled,
      retry: false,
      refetchInterval: (response) =>
        response?.data?.some(application => application.status === 'documents_pending') ? 15000 : false,
    }
  );
}
export function useInscricaoAdmin(id) {
  const { faculdade, token, user } = usePortal(),
    context = usePortalContext();
  return useQuery(
    ['portal', faculdade, user?.id, 'inscricao', id],
    () => portalRequest(faculdade, token, 'inscricoes/' + id).then((r) => r.data),
    {
      enabled: !!context.data && !!id,
      retry: false,
      refetchInterval: (data) =>
        ['pending', 'processing'].includes(data?.integration_status) ? 5000
          : data?.status === 'documents_pending' ? 15000 : false,
    }
  );
}
function mutationPermission(path, method) {
  let module =
    path.startsWith('selecoes') || path.startsWith('sincronizacoes')
      ? 'portal_configuracoes'
      : 'portal_inscricoes';
  if (path.includes('/documentos/') || path.includes('/convocacoes')) module = 'portal_documentos';
  if (path.includes('/recursos/')) module = 'portal_recursos';
  if (path.includes('/reprocessar')) module = 'portal_integracoes';
  if (path.startsWith('exportacoes')) module = 'portal_relatorios';
  const action =
    method === 'DELETE'
      ? 'delete'
      : method === 'POST' && ['selecoes', 'exportacoes'].includes(path)
      ? 'create'
      : 'edit';
  return {
    module,
    action,
  };
}
export function usePortalMutation(path, method = 'POST') {
  const { faculdade, token, user } = usePortal(),
    cache = useQueryClient();
  const permission = mutationPermission(
    typeof path === 'function' ? 'selecoes/item' : path,
    method
  );
  const allowed =
    can(user, permission.module, permission.action) ||
    (typeof path === 'string' &&
      path.startsWith('operacoes') &&
      can(user, 'portal_integracoes', permission.action));
  const mutation = useMutation(
    (data) => {
      const target = typeof path === 'function' ? path(data) : path;
      const required = mutationPermission(target, method);
      if (
        !can(user, required.module, required.action) &&
        !(target.startsWith('operacoes') && can(user, 'portal_integracoes', required.action))
      )
        throw new Error('Você não possui permissão para esta ação.');
      return portalRequest(faculdade, token, target, method, data);
    },
    {
      onSuccess: () =>
        cache.invalidateQueries({
          queryKey: ['portal', faculdade, user?.id],
          predicate: (q) => q.queryKey[3] !== 'context',
        }),
    }
  );
  return {
    ...mutation,
    allowed,
  };
}
export function usePortalDownload() {
  const { faculdade, token, user } = usePortal();
  const allowed = (path) => can(user, mutationPermission(path, 'GET').module, 'view');
  const mutation = useMutation(({ path }) => {
    if (!allowed(path)) throw new Error('Você não possui permissão para acessar este documento.');
    return portalFile(faculdade, token, path);
  });
  return {
    ...mutation,
    allowed,
  };
}
export function usePortalDocumentPreview(path) {
  const { faculdade, token, user } = usePortal();
  const allowed = can(user, 'portal_documentos', 'view');
  const mutation = useMutation(() => {
    if (!allowed) throw new Error('Você não possui permissão para acessar este documento.');
    return portalRequest(faculdade, token, path).then(response => response.data.url);
  });
  return { ...mutation, allowed };
}
export function useExportStatus(id) {
  const { faculdade, token, user } = usePortal();
  return useQuery(
    ['portal', faculdade, user?.id, 'export', id],
    () => portalRequest(faculdade, token, 'exportacoes/' + id).then((r) => r.data),
    {
      enabled: !!id && can(user, 'portal_relatorios', 'view'),
      retry: false,
      refetchInterval: (data) =>
        !data || ['pending', 'processing'].includes(data.status) ? 3000 : false,
    }
  );
}
export function useExportDownload() {
  const { faculdade, token, user } = usePortal();
  return useMutation((id) =>
    portalRequest(faculdade, token, 'exportacoes/' + id + '/download').then((r) => r.data.url)
  );
}
export function usePortalSettings(organizationId) {
  const { faculdade: currentOrganization, token, user } = usePortal();
  const faculdade = organizationId || currentOrganization;
  return useQuery(
    ['portal', faculdade, user?.id, 'configuration'],
    () => portalRequest(faculdade, token, 'configuracao').then((r) => r.data),
    {
      enabled: user?.role === 'region_admin' && can(user, 'portal_configuracoes', 'view'),
      retry: false,
    }
  );
}
export function useSavePortalSettings(organizationId) {
  const { faculdade: currentOrganization, token, user } = usePortal(),
    faculdade = organizationId || currentOrganization,
    cache = useQueryClient();
  const allowed = user?.role === 'region_admin' && can(user, 'portal_configuracoes', 'edit');
  const mutation = useMutation(
    (data) => {
      if (!allowed) throw new Error('Você não possui permissão para configurar este portal.');
      return portalRequest(faculdade, token, 'configuracao', 'PUT', data);
    },
    {
      onSuccess: () =>
        Promise.all([
          cache.invalidateQueries(['portal', faculdade, user?.id]),
          cache.invalidateQueries(['organizadora', user?.id]),
        ]),
    }
  );
  return {
    ...mutation,
    allowed,
  };
}
export function usePortalQuery(path, { enabled = true, poll = false } = {}) {
  const { faculdade, token, user } = usePortal(),
    context = usePortalContext();
  return useQuery(
    ['portal', faculdade, user?.id, 'request', path],
    () => portalRequest(faculdade, token, path),
    {
      enabled: !!context.data && enabled,
      retry: false,
      refetchInterval: poll
        ? (response) =>
            poll === 'always' ? 10000 : ['queued', 'processing', 'pending'].includes(response?.data?.status) ? 3000 : false
        : false,
    }
  );
}
